import React, { useState } from "react";
import { COMPONENT_TYPES } from "../../../../pages/constants/AiAgent";
import { ComponentActionButton } from "./ComponentActionButton";
import { useComponentAction } from "./useComponentAction";
import {
  HiOutlineBriefcase,
  HiOutlineMapPin,
  HiOutlineClock,
  HiOutlineEnvelope,
  HiOutlinePhone,
  HiOutlineAcademicCap,
  HiOutlineDocumentText,
  HiOutlineStar,
  HiOutlineChatBubbleLeft,
} from "react-icons/hi2";

const DetailRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className="w-3.5 h-3.5 text-textGray mt-0.5 flex-shrink-0" />
      <div>
        <span className="text-textGray">{label}: </span>
        <span className="text-primaryBlack font-medium">{value}</span>
      </div>
    </div>
  );
};

const ScoreBadge = ({ score }) => {
  if (score == null) return null;
  const color =
    score >= 80
      ? "bg-green-100 text-green-700 border-green-200"
      : score >= 60
      ? "bg-yellow-100 text-yellow-700 border-yellow-200"
      : "bg-red-100 text-red-700 border-red-200";
  return (
    <div className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border ${color}`}>
      Resume Score: {score}%
    </div>
  );
};

const StarRating = ({ rating }) => {
  if (rating == null) return null;
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <HiOutlineStar
          key={star}
          className={`w-3.5 h-3.5 ${
            star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
          }`}
        />
      ))}
    </div>
  );
};

const CandidateDetailCard = ({ payload, disabled }) => {
  const { data, actions } = payload;
  const { sendAction } = useComponentAction();
  const [actionsDisabled, setActionsDisabled] = useState(disabled);

  const handleAction = (action, params) => {
    setActionsDisabled(true);
    sendAction(COMPONENT_TYPES.CANDIDATE_DETAIL_CARD, action, params);
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white w-full">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold text-primaryBlack">{data.name}</h3>
          {data.profile_name && (
            <p className="text-xs text-blue-600 mt-0.5">
              Applied for: {data.profile_name}
            </p>
          )}
          <p className="text-xs text-textGray mt-0.5">
            {data.current_title}
            {data.current_company && ` @ ${data.current_company}`}
          </p>
          {data.location && (
            <p className="text-xs text-textGray mt-0.5 flex items-center gap-1">
              <HiOutlineMapPin className="w-3 h-3" /> {data.location}
            </p>
          )}
        </div>
        <ScoreBadge score={data.resume_score} />
      </div>

      {/* Contact */}
      <div className="flex flex-col gap-1.5 mt-3 pt-2 border-t border-gray-100">
        <DetailRow icon={HiOutlineEnvelope} label="Email" value={data.email} />
        <DetailRow icon={HiOutlinePhone} label="Phone" value={data.phone} />
      </div>

      {/* Professional */}
      <div className="flex flex-col gap-1.5 mt-3 pt-2 border-t border-gray-100">
        <DetailRow icon={HiOutlineClock} label="Experience" value={data.experience} />
        <DetailRow icon={HiOutlineAcademicCap} label="Qualification" value={data.qualification} />
        <DetailRow icon={HiOutlineMapPin} label="Preferred Location" value={data.preferred_location} />
        <DetailRow icon={HiOutlineBriefcase} label="Notice Period" value={data.notice_period} />
      </div>

      {/* Skills */}
      {data.skills && (
        <div className="flex flex-wrap gap-1 mt-3 pt-2 border-t border-gray-100">
          {data.skills.split(",").map((skill, i) => (
            <span
              key={i}
              className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded"
            >
              {skill.trim()}
            </span>
          ))}
        </div>
      )}

      {/* Assessment */}
      {(data.resume_score != null || data.rating != null) && (
        <div className="mt-3 pt-2 border-t border-gray-100">
          {data.rating != null && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-textGray">Rating:</span>
              <StarRating rating={data.rating} />
            </div>
          )}
        </div>
      )}

      {/* Notes & Feedback */}
      {(data.notes || data.feedback) && (
        <div className="mt-3 pt-2 border-t border-gray-100 flex flex-col gap-1.5">
          {data.notes && (
            <DetailRow icon={HiOutlineDocumentText} label="Notes" value={data.notes} />
          )}
          {data.feedback && (
            <DetailRow icon={HiOutlineChatBubbleLeft} label="Feedback" value={data.feedback} />
          )}
        </div>
      )}

      {/* Actions */}
      {actions?.length > 0 && (
        <div className="flex gap-2 mt-3 pt-2 border-t border-gray-100">
          {actions.map((action) => (
            <ComponentActionButton
              key={action.action}
              {...action}
              onAction={handleAction}
              disabled={actionsDisabled}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CandidateDetailCard;
